import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import React, { useEffect, useState } from 'react'
import { useSearchData } from 'Components/General/SearchProvider/SearchProvider'
import { useTranslation } from 'react-i18next'
import CombinedSearch from './CombinedSearch'
import { IError } from 'types/error'
import { ISearchLanguage, ISearchSkill } from 'types/searchInterfaces'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import { skillAPI } from 'api/SkillAPI'
import { ICertificate, ICompetence } from 'types/cvsInterfaces'
import { IIndustry, IRole, ISkill } from 'types/levelItemInterfaces'
import LoadingIndicator from 'Components/reusable/LoadingIndicator'
import Results from './Results'
import colors from 'constants/colors'
import { certificateAPI } from 'api/CertificateAPI'
import { competenceAPI } from 'api/CompetenceAPI'
import { useLanguageData, useUser } from 'hooks'
import { useLocation } from 'react-router'

export { convertToTokens } from './tokenUtils'

/**
 * Renders the search page.
 *
 * @return {React.ReactElement} The rendered search component.
 */
const Search: React.FC = () => {
  const { t } = useTranslation()
  const { searchData, setSearchData } = useSearchData()
  const { features } = useUser()
  const { state } = useLocation()
  const { languages } = useLanguageData()

  const [loading, setLoading] = useState<boolean>(false)
  const [backendError, setBackendError] = useState<IError>()
  const [skills, setSkills] = useState<ISkill[]>([])
  const [industries, setIndustries] = useState<IIndustry[]>([])
  const [roles, setRoles] = useState<IRole[]>([])
  const [certificates, setCertificates] = useState<ICertificate[]>([])
  const [competences, setCompetences] = useState<ICompetence[]>([])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        const data = await skillAPI.getAll(controller)
        const certificates = await certificateAPI.getAll(controller)
        const competences = features.includes('competences') ? await competenceAPI.getAll(controller) : []
        const skillItems: ISkill[] = []
        const roleItems: IRole[] = []
        const industryItems: IIndustry[] = []
        for (const item of data) {
          if (item.kind === 'skill') {
            skillItems.push(item)
          } else if (item.kind === 'role') {
            roleItems.push(item)
          } else if (item.kind === 'industry') {
            industryItems.push(item)
          }
        }

        setSkills(skillItems)
        setRoles(roleItems)
        setIndustries(industryItems)
        setCertificates(certificates)
        setCompetences(competences)
      } catch (error) {
        setBackendError(error as IError)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [features])

  useEffect(() => {
    if (state && state.search) {
      if (state.search.skills && state.search.skills.length) {
        const newSkillTerms: ISearchSkill[] = []
        for (const skill of state.search.skills) {
          newSkillTerms.push({
            name: skill.name,
            experienceMonths: parseInt(skill.experienceMonths, 10),
            level: parseInt(skill.level),
            required: skill.required,
            skillId: skill.skillId,
            Skill: skill.Skill,
          })
        }
        setSearchData(prevState => ({
          ...prevState,
          terms: {
            ...prevState.terms,
            skills: newSkillTerms,
          },
        }))
      }
      if (state.search.languages && state.search.languages.length && languages) {
        const newLanguageTerms: ISearchLanguage[] = []
        for (const language of state.search.languages) {
          if (languages?.[language.languageCode] !== undefined) {
            newLanguageTerms.push({
              level: language.level,
              languageCode: language.languageCode,
              name: languages?.[language.languageCode],
            })
          }
        }
        setSearchData(prevState => ({
          ...prevState,
          terms: {
            ...prevState.terms,
            languages: newLanguageTerms,
          },
        }))
      }
      if (state.search.startDate || state.search.endDate) {
        setSearchData(prevState => ({
          ...prevState,
          terms: {
            ...prevState.terms,
            startDate: new Date(state.search.startDate),
            endDate: state.search.endDate ? new Date(state.search.endDate) : undefined,
          },
        }))
      }
      if (state.search.query) {
        setSearchData(prevState => ({
          ...prevState,
          terms: {
            ...prevState.terms,
            query: state.search.query,
          },
        }))
      }
    }
  }, [languages, setSearchData, state])

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <Box
      px={2}
      pb={2}
      sx={{
        maxWidth: 1000,
        backgroundColor: colors.searchBackground,
        margin: '0 auto',
        backgroundImage: 'none',
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', textTransform: 'none' }} fontWeight="bold">
        {t('search.combinedSearch')}
      </Typography>
      <CombinedSearch
        loading={loading}
        setLoading={setLoading}
        skillOptions={skills}
        roleOptions={roles}
        industryOptions={industries}
        certificateOptions={certificates}
        competenceOptions={competences}
      />
      {loading && <LoadingIndicator />}
      {searchData.results && !loading && <Results />}
    </Box>
  )
}

export default Search
